import { useState, useCallback } from 'react';
import { Upload, X, FileText, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type UploadStatus =
  | { state: 'pending' }
  | { state: 'uploaded'; documentId: string; filePath: string }
  | { state: 'error'; error: Error };

export interface UploadEntry {
  clientId: string;
  file: File;
  status: UploadStatus;
}

interface DocumentUploadProps {
  onFilesSelected: (files: File[]) => void;
  entries: UploadEntry[];
  onRemoveEntry: (clientId: string) => void;
  maxFiles?: number;
  acceptedTypes?: string[];
}

const DEFAULT_ACCEPTED_TYPES = ['.pdf', '.docx', '.xlsx', '.xls', '.pptx', '.txt', '.md', '.csv'];

export function DocumentUpload({
  onFilesSelected,
  entries,
  onRemoveEntry,
  // 50 is well above any realistic single-batch upload (the demo corpus is
  // 21) but low enough that an accidental folder-drop doesn't try to
  // ingest a whole drive. Bump if real users hit it.
  maxFiles = 50,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
}: DocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = filterValidFiles(droppedFiles);

    if (validFiles.length > 0) {
      const remaining = Math.max(0, maxFiles - entries.length);
      onFilesSelected(validFiles.slice(0, remaining));
    }
  }, [entries.length, maxFiles, onFilesSelected]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);
    const validFiles = filterValidFiles(selectedFiles);

    if (validFiles.length > 0) {
      const remaining = Math.max(0, maxFiles - entries.length);
      onFilesSelected(validFiles.slice(0, remaining));
    }

    // Reset input so the same file can be selected again
    e.target.value = '';
  }, [entries.length, maxFiles, onFilesSelected]);

  const filterValidFiles = (fileList: File[]): File[] => {
    return fileList.filter(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return acceptedTypes.includes(extension);
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderStatus = (status: UploadStatus) => {
    switch (status.state) {
      case 'pending':
        return (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Uploading…</span>
          </div>
        );
      case 'uploaded':
        return (
          <div className="flex items-center gap-1.5 text-xs text-green-600">
            <CheckCircle2 className="h-3 w-3" />
            <span>Uploaded</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center gap-1.5 text-xs text-red-600" title={status.error.message}>
            <AlertCircle className="h-3 w-3" />
            <span>Failed to upload</span>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
          isDragging
            ? 'border-ingesta-blue-500 bg-ingesta-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        )}
      >
        <input
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="flex flex-col items-center gap-3 cursor-pointer"
        >
          <Upload className="h-10 w-10 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-700">
              {isDragging ? 'Drop files here' : 'Drag and drop files, or click to browse'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Supports PDF, DOCX, XLSX, TXT, MD, CSV (max {maxFiles} files)
            </p>
          </div>
        </label>
      </div>

      {/* File List */}
      {entries.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            {entries.length} file{entries.length !== 1 ? 's' : ''} selected
          </p>
          <ul className="max-h-[40vh] overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {entries.map((entry) => (
              <li
                key={entry.clientId}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {entry.file.name}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <p className="text-xs text-gray-500">
                        {formatFileSize(entry.file.size)}
                      </p>
                      {renderStatus(entry.status)}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveEntry(entry.clientId)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
